import type {
  AutocompleteResponse,
  GroupDetailResponse,
  GroupListResponse,
  GroupTleBundle,
  PassesResponse,
  SatelliteDetailResponse,
  SatelliteListResponse,
  SatelliteRadioResponse,
  SearchResponse,
  SpaceWeather24hResponse,
  SpaceWeatherNowResponse,
  TleResponse,
} from './types';

const BASE = '/api/v1';

/**
 * Thrown for any non-2xx API response. `body` is the raw text (usually
 * the JSON `{error:{...}}` shape, but parsing is left to the caller in
 * case the response wasn't JSON).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    public readonly url: string,
  ) {
    super(`API ${status} for ${url}`);
    this.name = 'ApiError';
  }
}

interface ListSatellitesQuery {
  country?: string | string[];
  operator?: string;
  type?: string | string[];
  status?: string | string[];
  orbit_class?: string | string[];
  launched_after?: string;
  launched_before?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export async function listSatellites(query: ListSatellitesQuery = {}): Promise<SatelliteListResponse> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return getJson(`/satellites?${params.toString()}`);
}

export async function getSatelliteDetail(norad: number): Promise<SatelliteDetailResponse> {
  return getJson(`/satellites/${norad}`);
}

export async function getSatelliteTle(norad: number): Promise<TleResponse> {
  return getJson(`/satellites/${norad}/tle`);
}

export async function getSatelliteRadio(norad: number): Promise<SatelliteRadioResponse> {
  return getJson(`/satellites/${norad}/radio`);
}

export async function listGroups(): Promise<GroupListResponse> {
  return getJson('/groups');
}

export async function getGroupDetail(slug: string): Promise<GroupDetailResponse> {
  return getJson(`/groups/${encodeURIComponent(slug)}`);
}

export async function getGroupTles(slug: string): Promise<GroupTleBundle> {
  return getJson<GroupTleBundle>(`/groups/${encodeURIComponent(slug)}/tles`);
}

// Phase 9 chunk 2 — GNSS constellation membership aggregate.
export interface GnssMembershipResponse {
  data: {
    gps:     number[];
    galileo: number[];
    glonass: number[];
    beidou:  number[];
  };
  meta: {
    computed_at: string;
    count:       number;
  };
}

export async function getGnssMembership(): Promise<GnssMembershipResponse> {
  return getJson<GnssMembershipResponse>('/gnss/membership');
}

// Post-Phase-11 — per-source ingest freshness for the About modal.
// Each value is { last_pulled_at: ISO-8601 string or null, row_count: int }.
export interface IngestStatusEntry {
  last_pulled_at: string | null;
  row_count:      number;
}
export interface IngestStatusResponse {
  tle_catalog:  IngestStatusEntry;
  launches:     IngestStatusEntry;
  reentries:    IngestStatusEntry;
  conjunctions: IngestStatusEntry;
}

export async function getIngestStatus(): Promise<IngestStatusResponse> {
  return getJson<IngestStatusResponse>('/health/ingest-status');
}

// Phase 9 chunk 4 — decay-trace bundle.
export interface DecayTracesResponse {
  data: Array<{
    norad_id:        number;
    name:            string;
    predicted_decay: string;
    line1:           string;
    line2:           string;
    tle_epoch:       string;
    risk_score:      number | null;
  }>;
  meta: {
    days:        number;
    count:       number;
    max_traces:  number;
    computed_at: string;
  };
}

export async function getDecayTraces(days: number = 30): Promise<DecayTracesResponse> {
  const params = new URLSearchParams({ days: String(days) });
  return getJson<DecayTracesResponse>(`/reentries/traces?${params.toString()}`);
}

// Phase 10 chunk 3 — amateur SatNOGS ground stations.
export interface AmateurStation {
  id:            string;
  name:          string;
  latitude_deg:  number;
  longitude_deg: number;
  altitude_m:    number | null;
  qf_locator:    string | null;
  qra_active:    boolean;
  last_seen_at:  string | null;
  details_url:   string;
}

export interface AmateurStationsResponse {
  data: AmateurStation[];
  meta: {
    page:         number;
    per_page:     number;
    total:        number;
    active_total: number;
    returned:     number;
  };
}

export async function getAmateurStations(
  page: number = 1,
  perPage: number = 1000,
  includeInactive: boolean = false,
): Promise<AmateurStationsResponse> {
  const params = new URLSearchParams({
    page:     String(page),
    per_page: String(perPage),
  });
  if (includeInactive) params.set('include_inactive', '1');
  return getJson<AmateurStationsResponse>(`/amateur-stations?${params.toString()}`);
}

export async function search(q: string): Promise<SearchResponse> {
  return getJson(`/search?q=${encodeURIComponent(q)}`);
}

export async function autocomplete(q: string): Promise<AutocompleteResponse> {
  return getJson(`/autocomplete?q=${encodeURIComponent(q)}`);
}

export interface PassesQuery {
  lat: number;
  lon: number;
  alt?: number;
  days?: number;
  min_elevation_deg?: number;
}

export async function getSatellitePasses(norad: number, query: PassesQuery): Promise<PassesResponse> {
  const params = new URLSearchParams();
  params.set('lat', String(query.lat));
  params.set('lon', String(query.lon));
  if (query.alt !== undefined) params.set('alt', String(query.alt));
  if (query.days !== undefined) params.set('days', String(query.days));
  if (query.min_elevation_deg !== undefined) params.set('min_elevation_deg', String(query.min_elevation_deg));
  return getJson(`/satellites/${norad}/passes?${params.toString()}`);
}

/** Phase 7 chunk 3A — sky-view marquee-pass aggregate. */
export interface SkyViewPass {
  norad: number;
  label: string;
  rise_at: string;
  peak_at: string;
  set_at: string;
  duration_seconds?: number;
  max_elevation_deg: number;
  magnitude?: number | null;
}

export interface SkyViewPassesResponse {
  data: SkyViewPass[];
  meta: {
    observer: { latitude: number; longitude: number };
    within_hours: number;
    /** Phase 7 chunk 4A — which roster was requested. */
    include: 'marquee' | 'bright';
    roster_size: number;
    /** Phase 7 chunk 4A — count of bright-pass-discovery rows in `data`
     *  (additive on top of the marquee subset). 0 when include=marquee. */
    bright_count: number;
    /** Phase 7 chunk 4A — hard cap on total returned rows. */
    max_arcs: number;
    count: number;
    computed_at: string;
  };
}

export async function getSkyViewPasses(query: {
  lat: number;
  lon: number;
  withinHours?: number;
  /** Phase 7 chunk 4A — `bright` adds RCS-estimated naked-eye candidates
   *  on top of the marquee roster (mag ≤ 4.5, capped at 100 total arcs).
   *  First call from a fresh observer location is slower (~3-5 s) while
   *  the per-NORAD pass-cache warms; subsequent calls are <100 ms. */
  include?: 'marquee' | 'bright';
}): Promise<SkyViewPassesResponse> {
  const params = new URLSearchParams();
  params.set('lat', String(query.lat));
  params.set('lon', String(query.lon));
  if (query.withinHours !== undefined) params.set('within_hours', String(query.withinHours));
  params.set('include', query.include ?? 'marquee');
  return getJson(`/sky-view/passes?${params.toString()}`);
}

export async function getSpaceWeatherNow(): Promise<SpaceWeatherNowResponse> {
  return getJson('/space-weather/now');
}

export async function getSpaceWeather24h(): Promise<SpaceWeather24hResponse> {
  return getJson('/space-weather/24h');
}

async function getJson<T>(path: string): Promise<T> {
  const url = `${BASE}${path}`;
  const r = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!r.ok) {
    throw new ApiError(r.status, await r.text(), url);
  }
  return (await r.json()) as T;
}
